import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { 
  X, 
  Upload, 
  Search, 
  Grid, 
  List, 
  ChevronLeft, 
  ChevronRight,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { wpMedia } from '../../lib/wordpress';
import { useDropzone } from 'react-dropzone';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { scrollToSection } from '../../utils/scroll';
import { Pagination } from '../shared/Pagination';

interface ImageBrowserProps {
  onSelect: (url: string) => void;
  onClose: () => void;
  isAdmin?: boolean;
}

interface WPImage {
  id: number;
  source_url: string;
  title: { rendered: string };
  media_details: {
    sizes?: {
      thumbnail?: { source_url: string };
      medium?: { source_url: string };
      large?: { source_url: string };
    };
    filesize?: number;
    length_formatted?: string;
    mime_type?: string;
  };
  date: string;
  mime_type: string;
}

export const ImageBrowser: React.FC<ImageBrowserProps> = ({ onSelect, onClose, isAdmin = false }) => {
  const [images, setImages] = useState<WPImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedImage, setSelectedImage] = useState<WPImage | null>(null);
  const [filter, setFilter] = useState<'all' | 'recent' | 'popular'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Buscando imagens do WordPress...');
      
      const response = await wpMedia.listMedia({
        search: searchQuery,
        page: currentPage,
        per_page: 20,
        media_type: 'image',
        orderby: sortBy === 'size' ? 'date' : sortBy === 'name' ? 'title' : 'date',
        order: sortOrder
      });

      console.log('Resposta do WordPress:', response);

      if (response && Array.isArray(response.items)) {
        console.log(`${response.items.length} imagens encontradas`);
        setImages(response.items);
        setTotalPages(response.totalPages || 1);
      } else {
        console.error('Formato de resposta inválido:', response);
        throw new Error('Formato de resposta inválido');
      }
    } catch (err) {
      console.error('Erro ao buscar imagens:', err);
      setError('Não foi possível carregar as imagens. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, currentPage, sortBy, sortOrder]);

  useEffect(() => {
    console.log('ImageBrowser montado, buscando imagens...');
    fetchImages();
  }, [fetchImages]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!isAdmin) return;

    setUploading(true);
    setError(null);

    try {
      for (const file of acceptedFiles) {
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} não é uma imagem válida`);
        }

        await wpMedia.uploadMedia(file);
      }

      await fetchImages();
    } catch (err) {
      console.error('Erro no upload:', err);
      setError('Erro ao fazer upload das imagens. Por favor, tente novamente.');
    } finally {
      setUploading(false);
    }
  }, [fetchImages, isAdmin]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    disabled: !isAdmin || uploading
  });

  const handleImageSelect = useCallback((image: WPImage) => {
    console.log('Imagem selecionada:', image);
    setSelectedImage(image);
    onSelect(image.source_url);
  }, [onSelect]);

  const handleClose = useCallback(() => {
    console.log('Fechando galeria de imagens');
    onClose();
  }, [onClose]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    scrollToSection('image-browser-content');
  };

  const filteredImages = images.filter(image => {
    if (filter === 'recent') {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return new Date(image.date) >= oneWeekAgo;
    }
    return true;
  });

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-[300] flex items-center justify-center"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-card/95 backdrop-blur-sm rounded-lg w-full max-w-6xl mx-auto shadow-2xl border border-primary/20 relative flex flex-col max-h-[90vh] z-[301]"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-6 bg-card/95 backdrop-blur-sm border-b border-primary/10">
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
            Galeria de Imagens
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary/80 rounded-full transition-all duration-200 hover:scale-110"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="p-4 md:p-6 border-b border-border flex flex-col md:flex-row gap-4 shrink-0 bg-card/95 backdrop-blur-sm">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Procurar imagens..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-secondary rounded-md"
            />
          </div>

          {/* View Toggle & Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 bg-secondary rounded-md p-1">
              <button
                onClick={() => setView('grid')}
                className={`p-1 rounded ${view === 'grid' ? 'bg-primary text-primary-foreground' : ''}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setView('list')}
                className={`p-1 rounded ${view === 'list' ? 'bg-primary text-primary-foreground' : ''}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'recent' | 'popular')}
              className="bg-secondary rounded-md px-3 py-2 text-sm"
            >
              <option value="all">Todas</option>
              <option value="recent">Recentes</option>
              <option value="popular">Populares</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'size')}
              className="bg-secondary rounded-md px-3 py-2 text-sm"
            >
              <option value="date">Data</option>
              <option value="name">Nome</option>
              <option value="size">Tamanho</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 hover:bg-secondary rounded-full transition-colors"
            >
              {sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div id="image-browser-content" className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0">
          {error && (
            <div className="bg-destructive/10 text-destructive p-4 rounded-md mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {isAdmin && (
                <div
                  {...getRootProps()}
                  className={`mb-4 border-2 border-dashed rounded-lg p-8 text-center transition-colors
                    ${isDragActive ? 'border-primary bg-primary/10' : 'border-border'}
                    ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary'}`}
                >
                  <input {...getInputProps()} />
                  <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  {uploading ? (
                    <p>A enviar imagens...</p>
                  ) : isDragActive ? (
                    <p>Solte as imagens aqui...</p>
                  ) : (
                    <p>Arraste imagens ou clique para selecionar</p>
                  )}
                </div>
              )}

              {view === 'grid' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredImages.map((image) => (
                    <motion.div
                      key={image.id}
                      layoutId={`image-${image.id}`}
                      className={`relative group cursor-pointer rounded-lg overflow-hidden
                        ${selectedImage?.id === image.id ? 'ring-2 ring-primary' : ''}`}
                      onClick={() => handleImageSelect(image)}
                    >
                      <img
                        src={image.source_url}
                        alt={image.title.rendered}
                        className="w-full h-48 object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity p-4 flex flex-col justify-end">
                        <p className="text-white text-sm font-medium truncate">
                          {image.title.rendered}
                        </p>
                        <p className="text-white/70 text-xs">
                          {format(new Date(image.date), "d 'de' MMMM, yyyy", { locale: pt })}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredImages.map((image) => (
                    <motion.div
                      key={image.id}
                      layoutId={`image-${image.id}`}
                      className={`flex items-center gap-4 p-2 rounded-lg hover:bg-secondary cursor-pointer
                        ${selectedImage?.id === image.id ? 'bg-secondary' : ''}`}
                      onClick={() => handleImageSelect(image)}
                    >
                      <img
                        src={image.source_url}
                        alt={image.title.rendered}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{image.title.rendered}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(image.date), "d 'de' MMMM, yyyy", { locale: pt })}
                        </p>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {image.media_details.filesize ? `${Math.round(image.media_details.filesize / 1024)}KB` : 'N/A'}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}; 